import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import thankYouBanner from "../../Assets/thankyou-banner.webp";
import "./ThankYou.css";
import { FaEnvelope } from "react-icons/fa6";

/* ---------- Celebratory Confetti Burst (Canvas) ---------- */
const ConfettiCanvas = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let animationFrameId;
    let width = (canvas.width = window.innerWidth);
    let height = (canvas.height = window.innerHeight);

    const handleResize = () => {
      if (!canvas) return;
      width = canvas.width = window.innerWidth;
      height = canvas.height = window.innerHeight;
    };
    window.addEventListener("resize", handleResize);

    const colors = ["#e6245c", "#3c1861", "#2a2d5a", "#ff4d7e", "#f26522", "#ffffff"];
    const particles = [];
    const particleCount = 95;

    // Launch celebratory bursts from both sides towards center
    for (let i = 0; i < particleCount; i++) {
      const isLeft = i % 2 === 0;
      particles.push({
        x: isLeft ? width * 0.12 : width * 0.88,
        y: height * 0.38,
        vx: (isLeft ? 1 : -1) * (Math.random() * 9 + 4),
        vy: -(Math.random() * 13 + 7),
        size: Math.random() * 9 + 4,
        color: colors[Math.floor(Math.random() * colors.length)],
        rotation: Math.random() * 360,
        rotationSpeed: (Math.random() - 0.5) * 10,
        gravity: 0.3,
        drag: 0.985,
        opacity: 1,
      });
    }

    const startTime = Date.now();
    const duration = 4200; // 4.2 seconds celebratory blast

    const render = () => {
      const elapsed = Date.now() - startTime;
      if (elapsed > duration) {
        ctx.clearRect(0, 0, width, height);
        return;
      }

      ctx.clearRect(0, 0, width, height);

      particles.forEach((p) => {
        p.vx *= p.drag;
        p.vy += p.gravity;
        p.x += p.vx;
        p.y += p.vy;
        p.rotation += p.rotationSpeed;
        p.opacity = Math.max(0, 1 - elapsed / duration);

        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate((p.rotation * Math.PI) / 180);
        ctx.globalAlpha = p.opacity;
        ctx.fillStyle = p.color;
        ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * 0.6);
        ctx.restore();
      });

      animationFrameId = requestAnimationFrame(render);
    };

    render();

    return () => {
      window.removeEventListener("resize", handleResize);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="ty-confetti-canvas"
      aria-hidden="true"
    />
  );
};

/* ---------- Scroll Reveal Component (Framer Motion) ---------- */
const Reveal = ({ children, className = "", delay = 0 }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 22 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.08 }}
      transition={{
        duration: 0.65,
        delay: delay / 1000,
        ease: [0.16, 1, 0.3, 1],
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const ThankYou = () => {
  useEffect(() => {
    // ─── Meta Pixel Conversion Tracking on Verified Thank You Page ───
    if (typeof window !== "undefined" && typeof window.fbq === "function") {
      try {
        window.fbq("track", "Purchase", { currency: "USD", value: 30.00 });
        console.log("[Meta Pixel] Purchase ($30.00 USD) successfully tracked on Thank You page");
      } catch (e) {
        console.error("Meta Pixel tracking error:", e);
      }
    }

    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  return (
    <div className="ty-page">
      <Helmet>
        <title>Thank You | Bharat Buildcon</title>
        <meta
          name="description"
          content="Thank you for registering with Bharat Buildcon. Your submission has been received successfully."
        />
      </Helmet>

      {/* Celebratory Confetti Particle Cannon */}
      <ConfettiCanvas />

      {/* Dynamic Ambient Blur Glow Orbs */}
      <div className="ty-bg-glow ty-bg-glow--saffron" />
      <div className="ty-bg-glow ty-bg-glow--blue" />
      <div className="ty-bg-glow ty-bg-glow--gold" />
      <div className="ty-bg-glow ty-bg-glow--emerald" />

      {/* ---------------- BREADCRUMB & ANIMATED HEADER ---------------- */}
      <section className="ty-header-section">
        <div className="ty-container">
          <Reveal>
            <div className="clean-breadcrumb-wrapper">
              <nav className="about-style-breadcrumb" aria-label="Breadcrumb">
                <Link to="/" className="bc-home-link">
                  HOME
                </Link>
                <span className="bc-slash">/</span>
                <span className="bc-active-page">THANK YOU</span>
              </nav>
            </div>
          </Reveal>

          {/* Animated SVG Success Stamp with Concentric Ripples */}
          <div className="ty-success-stamp-wrap">
            <div className="ty-ripple ty-ripple--1" />
            <div className="ty-ripple ty-ripple--2" />
            <div className="ty-success-circle">
              <svg className="ty-check-svg" viewBox="0 0 52 52">
                <circle className="ty-check-circle" cx="26" cy="26" r="23" fill="none" />
                <path className="ty-check-path" fill="none" d="M14 27l7 7 16-16" />
              </svg>
            </div>
          </div>

          <Reveal delay={120}>
            <h1 className="ty-page-title">
              Thank <span className="ty-title-gradient">you</span>
            </h1>
          </Reveal>

          <Reveal delay={200}>
            <div className="ty-status-pill">
              <span className="ty-live-dot" />
              <span>Registration Confirmed & Verified</span>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ---------------- FEATURED BANNER CARD WITH LUXURY SHIMMER ---------------- */}
      <section className="ty-banner-section">
        <div className="ty-container">
          <motion.div
            initial={{ opacity: 0, scale: 0.92, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{
              duration: 0.8,
              delay: 0.2,
              ease: [0.16, 1, 0.3, 1],
            }}
            className="ty-banner-card-wrapper"
          >
            <div className="ty-banner-card">
              {/* Shimmer light sweep animation */}
              <div className="ty-banner-shimmer" />
              <img
                src={thankYouBanner}
                alt="Thank You for Registering - Bharat Buildcon at Yashobhoomi"
                className="ty-banner-img"
                loading="eager"
              />
            </div>
          </motion.div>

          {/* Submission Confirmation Text Below Banner */}
          <Reveal delay={300}>
            <div className="ty-confirmation-text-wrap">
              <h2 className="ty-conf-heading">Thank you for registering!</h2>
              <p className="ty-conf-subheading">
                Your submission has been received successfully
              </p>
              <div className="ty-conf-email-note">
                <FaEnvelope className="ty-conf-mail-icon" />
                <span>A confirmation and official event pass will be delivered to your registered email.</span>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ---------------- INVEST IN INDIA SECTION (CLIENT REFERENCE) ---------------- */}
      <section className="ty-invest-section">
        <div className="ty-container">
          <Reveal delay={200}>
            <div className="ty-invest-card">
              <div className="ty-invest-body">
                <h2 className="ty-invest-title">
                  Invest in India
                </h2>
                <p className="ty-invest-subtitle">
                  Where Opportunities Await!
                </p>
                <div className="ty-invest-btn-row">
                  <Link to="/aboutus" className="ty-invest-btn ty-invest-btn--white">
                    GET STARTED
                  </Link>
                  <Link to="/contactus" className="ty-invest-btn ty-invest-btn--dark">
                    CONTACT US
                  </Link>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default ThankYou;
